import React, { useState } from 'react'
import classnames from 'classnames'

import { Text } from 'marketing-site/src/library/elements/Text'
import { Icon, mq, renderIconSVG, useMediaQuery } from '../../utils'
import { ImageOrAutoplayVideo } from '../ImageOrAutoplayVideo'
import SupportSpotlightFeaturedCard from './SupportSpotlightFeaturedCard'
import { IProps } from './index'

export const testIds = {
  card: 'support-spotlight-card',
  lottie: 'support-spotlight-card-video',
  body: 'support-spotlight-card-body',
  growIcon: 'support-spotlight-card-grow-icon',
}

export const SupportSpotlightCard: React.FC<IProps> = (props) => {
  const {
    title,
    description,
    video,
    fallbackImageRef,
    isFeaturedCard = false,
    isExpanded = false,
    isVisible,
  } = props

  const [isOnHover, setIsOnHover] = useState(false)

  const isLargeDesktop = useMediaQuery(`(${mq.desktopLg})`)

  const getAnimationState = () => {
    const isExpandedAndVisible =
      typeof isVisible === 'boolean' ? isExpanded && isVisible : isExpanded
    if (isExpandedAndVisible || isOnHover) return 'play'
    return 'stop'
  }

  const handleOnMouseEnter = () => {
    if (isExpanded) return
    setIsOnHover(true)
  }

  const handleOnMouseLeave = () => {
    setIsOnHover(false)
  }

  const videoClasses = classnames('support-spotlight-card__video', {
    'support-spotlight-card__video--zoomed': !isExpanded && !isOnHover,
  })

  const bodyClasses = classnames('support-spotlight-card__body', {
    'support-spotlight-card__body--floating': isExpanded,
    'support-spotlight-card__body--fixed': !isExpanded,
    'support-spotlight-card__body--hide': !isExpanded && isOnHover,
  })

  const growIconClasses = classnames('support-spotlight-card__grow-icon', {
    'support-spotlight-card__grow-icon--hide': isExpanded || !isOnHover,
  })

  if (isFeaturedCard) {
    return <SupportSpotlightFeaturedCard {...props} />
  }

  return (
    <>
      <div
        className="support-spotlight-card__wrapper"
        onMouseEnter={handleOnMouseEnter}
        onMouseLeave={handleOnMouseLeave}
        data-testId={testIds.card}
      >
        <div className={videoClasses} data-testId={testIds.lottie}>
          <ImageOrAutoplayVideo
            video={video}
            imageRef={fallbackImageRef}
            forcedAnimationState={getAnimationState()}
          />
        </div>

        <div className={bodyClasses} data-testId={testIds.body}>
          <div className="heading">
            <Text brand="default" size={isLargeDesktop ? 'body-bold' : 'caption-bold'}>
              {title}
            </Text>
          </div>
          <div className="subheading">
            <Text brand="default" size={isLargeDesktop ? 'body' : 'caption'}>
              {description}
            </Text>
          </div>
        </div>

        <span className={growIconClasses} data-testId={testIds.growIcon}>
          {renderIconSVG(Icon.Grow, 'support-spotlight-card__grow-icon-svg')}
        </span>
      </div>

      <style jsx>{`
        .support-spotlight-card {
          &__wrapper {
            display: flex;
            width: 100%;
            border-radius: 25px;
            overflow: hidden;
            flex-direction: column;
            cursor: pointer;
            position: relative;
            -webkit-mask-image: -webkit-radial-gradient(white, black);
          }

          &__body {
            position: absolute;
            background-color: $white;
            padding: 1.375rem 1.75rem;
            transition: transform 400ms;

            &--floating {
              bottom: 1.75rem;
              left: 1.75rem;
              border-radius: 7px;
            }

            &--fixed {
              bottom: 0;
              left: 0;
              right: 0;
              transform: translateY(0);
            }

            &--hide {
              transform: translateY(100%);
            }

            .heading {
              margin-bottom: 0.375rem;
            }
          }

          &__video {
            overflow: hidden;
            transform: scale(1);
            transition: transform 400ms;

            &--zoomed {
              transform: scale(1.5);
            }
          }

          &__grow-icon {
            position: absolute;
            bottom: 1.75rem;
            left: 1.75rem;
            opacity: 1;
            transform: scale(1);
            transition: opacity, transform 300ms;

            &--hide {
              opacity: 0;
              transform: scale(0.5);
            }
          }
        }

        @media (${mq.desktopLg}) {
          .support-spotlight-card {
            &__body {
              padding: 2rem 2.5rem;
            }
          }
        }

        :global(.support-spotlight-card__grow-icon-svg) {
          box-shadow: 0px 1px 8px rgba(0, 0, 0, 0.1);
        }
      `}</style>
    </>
  )
}
